import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import mapboxgl from 'mapbox-gl'
import { MobXProviderContext, observer } from 'mobx-react'
import { MapRef, Marker } from 'react-map-gl/mapbox'

import MapCanvas from '~/client/src/shared/components/MapBoxViewer/MapCanvas'
import MapCardCarousel from '~/client/src/shared/components/MapCardCarousel/MapCardCarousel'
import {
  MapControls,
  MapControlsStore,
} from '~/client/src/shared/components/MapControls'
import StruxTagMapMarker from '~/client/src/shared/components/MapMarker/StruxTagMapMarker'
import { ResourceCard } from '~/client/src/shared/components/ResourceCardList/ResourceCard'
import { buildResourceCardModel } from '~/client/src/shared/components/ResourceCardList/resourceCardMapper'
import { IResourceRecord } from '~/client/src/shared/components/ResourceCardList/types'
import Config from '~/client/src/shared/Config'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  IStruxTagMapMarker,
  useStruxTagsMapMarkers,
} from '~/client/src/shared/resources/hooks/useStruxTagsMapMarkers'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'

import {
  useMobileResourceActions,
  useMobileResourceMeta,
  useMobileResourceState,
} from '../../components/MobileResource/context'

interface MarkerWithRecord {
  marker: IStruxTagMapMarker
  record: IResourceRecord
}

interface IMapViewport {
  latitude: number
  longitude: number
  zoom: number
}

function useProjectViewport(): IMapViewport | null {
  const { state } = React.useContext(MobXProviderContext) as any
  const center = state?.projectAddress?.center
  if (!center?.lat || !center?.lng) return null
  return {
    latitude: center.lat,
    longitude: center.lng,
    zoom: state?.projectAddress?.zoom || 12,
  }
}

const MobileStruxTagsMap: React.FC = () => {
  const viewport = useProjectViewport()

  if (!viewport) {
    return <div className="tw:w-full tw:h-full tw:min-h-0" />
  }

  return <MobileStruxTagsMapContent viewport={viewport} />
}

// observer: the props below read mapControlsStore, so a Layers toggle must re-render this.
const MobileStruxTagsMapContent: React.FC<{ viewport: IMapViewport }> =
  observer(({ viewport }) => {
    const { filteredRecords, cardConfiguration, properties, getDisplayValue } =
      useMobileResourceState()
    const { selectRecord } = useMobileResourceActions()
    const { resourceTypeId } = useMobileResourceMeta()

    const markers = useStruxTagsMapMarkers(filteredRecords, resourceTypeId)
    const mapRef = useRef<MapRef | null>(null)
    const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(
      null,
    )

    const markersWithRecords = useMemo((): MarkerWithRecord[] => {
      const recordMap = new Map(filteredRecords.map(r => [r.id, r]))
      return markers
        .map(marker => {
          const record = recordMap.get(marker.id)
          if (!record) return null
          return { marker, record }
        })
        .filter(Boolean) as MarkerWithRecord[]
    }, [markers, filteredRecords])

    const flyToMarker = useCallback(
      (markerId: string) => {
        const entry = markersWithRecords.find(m => m.marker.id === markerId)
        if (!entry) return
        mapRef.current?.getMap()?.flyTo({
          center: [entry.marker.longitude, entry.marker.latitude],
          duration: 800,
          essential: true,
        })
      },
      [markersWithRecords],
    )

    const handleCarouselChange = useCallback(
      (id: string) => {
        setSelectedMarkerId(id)
        flyToMarker(id)
      },
      [flyToMarker],
    )

    const handleMarkerClick = useCallback((id: string) => {
      setSelectedMarkerId(id)
    }, [])

    const handleCardClick = useCallback(
      (item: MarkerWithRecord) => {
        setSelectedMarkerId(item.marker.id)
        selectRecord(item.record.id)
      },
      [selectRecord],
    )

    const keyExtractor = useCallback(
      (item: MarkerWithRecord) => item.marker.id,
      [],
    )

    const renderCard = useCallback(
      (item: MarkerWithRecord) => {
        const model = buildResourceCardModel({
          record: item.record,
          resourceTypeId,
          cardConfiguration,
          properties,
          getDisplayValue,
        })

        return (
          <ResourceCard.Standard
            model={model}
            onClick={() => handleCardClick(item)}
            leading={renderResourceSemanticIcon(
              'sensor',
              'tw:block tw:size-full tw:text-muted-foreground',
            )}
          />
        )
      },
      [
        resourceTypeId,
        cardConfiguration,
        properties,
        getDisplayValue,
        handleCardClick,
      ],
    )

    const { latitude, longitude, zoom } = viewport
    const mapboxAccessToken = mapboxgl.accessToken || Config.MAPBOX_TOKEN

    const [mapControlsStore] = useState(() => new MapControlsStore(viewport))

    useEffect(() => () => mapControlsStore.dispose(), [mapControlsStore])

    const handleMapRef = useCallback(
      (ref: MapRef | null) => {
        mapRef.current = ref
        mapControlsStore.setMapRef(ref)
      },
      [mapControlsStore],
    )

    return (
      <div className="tw:w-full tw:h-full tw:min-h-0">
        <MapControls.Provider store={mapControlsStore} anchor="top" isMobile>
          <MapCanvas
            ref={handleMapRef}
            initialViewState={{ longitude, latitude, zoom }}
            style={{ width: '100%', height: '100%' }}
            mapStyle={mapControlsStore.mapStyle}
            mapboxAccessToken={mapboxAccessToken}
            projection={mapControlsStore.projection}
            isTrafficEnabled={mapControlsStore.globeViewStyle.isTrafficEnabled}
            isTerrainEnabled={mapControlsStore.globeViewStyle.isTerrainEnabled}
            isThreeDEnabled={mapControlsStore.globeViewStyle.isThreeDEnabled}
            shouldHideLabels={mapControlsStore.globeViewStyle.shouldHideLabels}
          >
            {markersWithRecords.map(({ marker }) => (
              <Marker
                key={marker.id}
                longitude={marker.longitude}
                latitude={marker.latitude}
                anchor="bottom"
              >
                <StruxTagMapMarker
                  isSelected={marker.id === selectedMarkerId}
                  title={marker.name || Localization.translator.struxTags}
                  onClick={() => handleMarkerClick(marker.id)}
                />
              </Marker>
            ))}
          </MapCanvas>
          <MapControls.Chrome />
          <MapControls.AboveAttribution>
            <MapCardCarousel
              items={markersWithRecords}
              selectedId={selectedMarkerId}
              keyExtractor={keyExtractor}
              renderCard={renderCard}
              onSelectedChange={handleCarouselChange}
            />
          </MapControls.AboveAttribution>
        </MapControls.Provider>
      </div>
    )
  })

export default MobileStruxTagsMap
